import { useEffect, useState } from 'react';
import Modal from './Modal';
import { spinnerRing } from '../utils/bookingUtils';
import { subscribeGlobalConfig, saveGlobalConfig, DEFAULT_GLOBAL_CONFIG, isPosSuperuser } from '../utils/posClientUtil';
import { POS_LOW_STOCK_MAX } from '../constants/posConst';
import { describeWriteError } from '../utils/firebaseErrorUtil';
import '../css/PosAdmin.css';

/**
 * Settings that apply to every client.
 *
 * Deliberately small. A setting belongs here only when it would be wrong for
 * two clients on this deployment to disagree about it — the currency they bill
 * in, whether a till left alone signs itself out, the line every receipt
 * carries. Anything that is genuinely a property of one club (its name, its
 * logo) lives on that client's own record instead.
 *
 * @param {object} props
 * @param {string} [props.signedInEmail] - the administrator on this session.
 *   Decides whether the low-stock level is offered: only a SUPERUSER may write
 *   it, and a field that is shown and then refused is worse than one that was
 *   never there.
 * @returns {JSX.Element}
 */
function PosGlobalConfigForm({ signedInEmail = '' }) {
  const canSetLowStock = isPosSuperuser(signedInEmail);
  const [form, setForm] = useState(DEFAULT_GLOBAL_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null);

  // Loaded once rather than kept live: a field that rewrote itself under the
  // cursor because another administrator saved would lose whatever was being
  // typed.
  useEffect(() => {
    let active = true;
    const stop = subscribeGlobalConfig((config) => {
      if (!active) return;
      setForm(config);
      setLoading(false);
      active = false;
    });
    return () => { active = false; stop(); };
  }, []);

  const update = (key) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await saveGlobalConfig(form, signedInEmail);
      setModal({ type: 'success', title: 'Config saved', message: 'These settings now apply to every client.' });
    } catch (error) {
      console.error('Saving global config failed:', error);
      setModal({
        type: 'error',
        title: 'Could not save',
        message: describeWriteError(error, { what: 'the config', path: 'posGlobalConfig' }),
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="pa-loading">{spinnerRing('Loading config')}</div>;
  }

  return (
    <div className="pa-screen">
      <header className="pa-screen-head">
        <div>
          <h1 className="pa-screen-title">Config</h1>
          <p className="pa-screen-sub">Applies to every client on this system.</p>
        </div>
      </header>

      <form className="pa-form" onSubmit={handleSubmit}>
        <div className="pa-form-grid">
          <label className="pa-field">
            <span className="pa-field-label">Currency symbol</span>
            <input
              className="pa-input"
              value={form.currencySymbol}
              onChange={update('currencySymbol')}
              disabled={saving}
              maxLength={4}
            />
            <span className="pa-field-hint">
              Printed in front of every amount. Display only — nothing is converted and no
              price is rewritten, so changing it tells the system what the existing numbers
              already meant.
            </span>
          </label>

          <label className="pa-field">
            <span className="pa-field-label">Currency name</span>
            <input
              className="pa-input"
              value={form.currencyDescription}
              onChange={update('currencyDescription')}
              disabled={saving}
              placeholder="Philippine Peso"
            />
            <span className="pa-field-hint">Describes the symbol for whoever edits this next. Not shown on the till.</span>
          </label>

          <div className="pa-field pa-field-wide">
            <span className="pa-field-label">Receipt payment note</span>
            <input
              className="pa-input"
              value={form.paymentNote}
              onChange={update('paymentNote')}
              disabled={saving}
            />
            <span className="pa-field-hint">
              Shown on the receipt preview before a sale is confirmed — the one instruction
              staff must not get wrong.
            </span>
          </div>

          {canSetLowStock && (
            <label className="pa-field">
              <span className="pa-field-label">Low-stock level</span>
              <input
                className="pa-input"
                type="number"
                min="0"
                max={POS_LOW_STOCK_MAX}
                step="1"
                value={form.lowStockThreshold ?? DEFAULT_GLOBAL_CONFIG.lowStockThreshold}
                onChange={(event) => setForm((current) => ({
                  ...current,
                  lowStockThreshold: event.target.value === '' ? '' : Number(event.target.value),
                }))}
                disabled={saving}
              />
              <span className="pa-field-hint">
                At or below this count, a product is flagged as running low — a banner on
                the till and a red figure on Inventory. Set it to 0 to turn the warning
                off; an empty shelf is still reported either way.
              </span>
              <span className="pa-field-hint">
                Only system@gmail.com can change this, here and in the database rules.
              </span>
            </label>
          )}

          <label className="pa-check pa-field-wide">
            <input
              type="checkbox"
              checked={form.adminIdleTimeoutEnabled !== false}
              onChange={update('adminIdleTimeoutEnabled')}
              disabled={saving}
            />
            <span>
              <span className="pa-field-label">Sign idle sessions out automatically</span>
              <span className="pa-field-hint">
                After 30 minutes without a tap — on this admin screen and on every client&rsquo;s
                till. A refresh does not sign anyone out either way; this is about a screen
                left alone.
              </span>
              <span className="pa-field-hint">
                Unticked, a signed-in till stays signed in until someone signs out, which is
                what a dedicated machine behind the counter wants. Leave it ticked where the
                till is shared or the public can reach it.
              </span>
            </span>
          </label>
        </div>

        <div className="pa-form-actions">
          <button type="submit" className="pa-btn pa-btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save config'}
          </button>
        </div>
      </form>

      <Modal
        isOpen={Boolean(modal)}
        onClose={() => setModal(null)}
        title={modal?.title || ''}
        message={modal?.message || ''}
        type={modal?.type || 'info'}
      />
    </div>
  );
}

export default PosGlobalConfigForm;
